import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import {
  BuildingOffice2Icon,
  UsersIcon,
  TruckIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  SignalIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon,
  DocumentTextIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { getSuperAdminDashboard, SuperAdminDashboardData } from '@/api/dashboardApi'

// Chart colors
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']
const PLAN_COLORS: Record<string, string> = {
  basic: '#9CA3AF',
  premium: '#3B82F6',
  enterprise: '#8B5CF6',
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

// Format large numbers
const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

// Growth badge component
const GrowthBadge: React.FC<{ growth: number }> = ({ growth }) => {
  const isPositive = growth >= 0
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {isPositive ? (
        <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
      ) : (
        <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
      )}
      {Math.abs(growth)}%
    </span>
  )
}

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<SuperAdminDashboardData | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      const response = await getSuperAdminDashboard()
      if (response.success && response.data) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Dashboard error:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No dashboard data available</p>
      </div>
    )
  }

  // Extract data
  const { overview, fleet, library, financial, rfid, attendance, security, charts, topInstitutions, recentInstitutions } = data

  // User distribution for pie chart
  const userDistribution = [
    { name: 'Students', value: overview.users.summary.students, color: '#3B82F6' },
    { name: 'Teachers', value: overview.users.summary.teachers, color: '#10B981' },
    { name: 'Parents', value: overview.users.summary.parents, color: '#F59E0B' },
    { name: 'Drivers', value: overview.users.summary.drivers, color: '#8B5CF6' },
    { name: 'School Admins', value: overview.users.summary.schoolAdmins, color: '#EC4899' },
  ].filter(item => item.value > 0)

  // Institution plan distribution
  const planDistribution = Object.entries(overview.institutions.byPlan || {}).map(([plan, count]) => ({
    name: plan.charAt(0).toUpperCase() + plan.slice(1),
    value: count,
    color: PLAN_COLORS[plan] || '#9CA3AF',
  }))

  // Terminal status distribution
  const terminalStatus = Object.entries(rfid.terminals.byStatus || {}).map(([status, count], index) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count,
    color: status === 'online' ? '#10B981' : status === 'offline' ? '#EF4444' : COLORS[index % COLORS.length],
  }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Platform Overview
          </h1>
          <p className="text-gray-600 mt-1">
            Welcome back, {user?.firstName}! Here's your platform status.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="secondary" onClick={fetchDashboardData}>
            <ClockIcon className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Link to="/admin/institutions">
            <Button>
              <BuildingOffice2Icon className="h-4 w-4 mr-2" />
              Manage Institutions
            </Button>
          </Link>
        </div>
      </div>

      {/* Primary Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Institutions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Institutions</p>
              <p className="text-2xl font-bold text-gray-900">{overview.institutions.total}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <BuildingOffice2Icon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-green-600">{overview.institutions.active} active</span>
            <GrowthBadge growth={overview.institutions.growth} />
          </div>
        </motion.div>

        {/* Total Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(overview.users.total)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <UsersIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-gray-500">+{overview.users.today} today</span>
            <GrowthBadge growth={overview.users.growth} />
          </div>
        </motion.div>

        {/* Revenue This Month */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Revenue (This Month)</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(financial.transactions.thisMonth.revenue)}</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-gray-500">{financial.transactions.thisMonth.count} transactions</span>
            <GrowthBadge growth={financial.transactions.revenueGrowth} />
          </div>
        </motion.div>

        {/* Outstanding Invoices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Outstanding Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(financial.invoices.outstanding.amount)}</p>
            </div>
            <div className={`p-3 rounded-lg ${financial.invoices.overdue.count > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
              <DocumentTextIcon className={`h-6 w-6 ${financial.invoices.overdue.count > 0 ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            {financial.invoices.overdue.count > 0 ? (
              <span className="text-red-600">
                <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                {financial.invoices.overdue.count} overdue
              </span>
            ) : (
              <span className="text-green-600">
                <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                No overdue invoices
              </span>
            )}
          </div>
        </motion.div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TruckIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{fleet.totalVehicles}</p>
              <p className="text-xs text-gray-600">Vehicles</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <BookOpenIcon className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{formatNumber(library.books.total)}</p>
              <p className="text-xs text-gray-600">Books</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <CreditCardIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{formatNumber(rfid.cards.total)}</p>
              <p className="text-xs text-gray-600">RFID Cards</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${rfid.terminals.byStatus?.online > 0 ? 'bg-green-100' : 'bg-gray-100'}`}>
              <SignalIcon className={`h-5 w-5 ${rfid.terminals.byStatus?.online > 0 ? 'text-green-600' : 'text-gray-600'}`} />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{rfid.terminals.total}</p>
              <p className="text-xs text-gray-600">Terminals</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <CurrencyDollarIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(financial.wallets.totalBalance)}</p>
              <p className="text-xs text-gray-600">Wallet Balance</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${fleet.maintenanceDue > 0 ? 'bg-orange-100' : 'bg-green-100'}`}>
              <WrenchScrewdriverIcon className={`h-5 w-5 ${fleet.maintenanceDue > 0 ? 'text-orange-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{fleet.maintenanceDue}</p>
              <p className="text-xs text-gray-600">Maintenance Due</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Registration Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">User Registration (30 Days)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={charts.userRegistration}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                stroke="#9CA3AF"
                fontSize={10}
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#3B82F6"
                fillOpacity={1}
                fill="url(#colorUsers)"
                name="New Users"
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Transaction Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Transaction Volume (30 Days)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={charts.transactions}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                stroke="#9CA3AF"
                fontSize={10}
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => formatCurrency(v)} />
              <Tooltip
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={(value: number) => [formatCurrency(value), '']}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Bar dataKey="credit" fill="#10B981" name="Credit" radius={[4, 4, 0, 0]} />
              <Bar dataKey="debit" fill="#EF4444" name="Debit" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={userDistribution}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
              >
                {userDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Count']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Institution Plans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Plans</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={planDistribution}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
              >
                {planDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => [value, 'Institutions']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Terminal Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Terminal Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={terminalStatus}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
              >
                {terminalStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => [value, 'Terminals']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Security & Library Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Security Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Security Overview</h3>
            <ShieldCheckIcon className="h-6 w-6 text-gray-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{security.sessions.active}</p>
              <p className="text-sm text-gray-600">Active Sessions</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{security.sessions.today}</p>
              <p className="text-sm text-gray-600">Logins Today</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{security.loginAttempts.failed}</p>
              <p className="text-sm text-gray-600">Failed Attempts</p>
            </div>
            <div className={`p-4 rounded-lg ${security.loginAttempts.failedToday > 10 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <p className={`text-2xl font-bold ${security.loginAttempts.failedToday > 10 ? 'text-red-600' : 'text-gray-600'}`}>
                {security.loginAttempts.failedToday}
              </p>
              <p className="text-sm text-gray-600">Failed Today</p>
            </div>
          </div>
        </motion.div>

        {/* Library & Attendance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Library & Attendance</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-cyan-50 rounded-lg">
              <p className="text-2xl font-bold text-cyan-600">{library.borrowings.active}</p>
              <p className="text-sm text-gray-600">Active Borrowings</p>
            </div>
            <div className={`p-4 rounded-lg ${library.borrowings.overdue > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
              <p className={`text-2xl font-bold ${library.borrowings.overdue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {library.borrowings.overdue}
              </p>
              <p className="text-sm text-gray-600">Overdue Books</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(library.fines.collected.amount)}</p>
              <p className="text-sm text-gray-600">Fines Collected</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(library.fines.pending.amount)}</p>
              <p className="text-sm text-gray-600">Pending Fines</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Attendance Rate</p>
                <p className="text-2xl font-bold text-gray-900">{attendance.attendance?.rate || 0}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Sessions This Month</p>
                <p className="text-lg font-semibold text-gray-900">{attendance.sessions.thisMonth}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Top Institutions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Top Institutions by Users</h3>
          <Link to="/admin/institutions">
            <Button variant="secondary" size="sm">
              <EyeIcon className="h-4 w-4 mr-1" />
              View All
            </Button>
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                <th className="pb-3 font-medium">Institution</th>
                <th className="pb-3 font-medium">Plan</th>
                <th className="pb-3 font-medium">Users</th>
                <th className="pb-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {topInstitutions.slice(0, 5).map((inst, index) => (
                <tr key={inst.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{inst.name}</p>
                        <p className="text-xs text-gray-500">{inst.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      inst.plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                      inst.plan === 'premium' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {inst.plan?.charAt(0).toUpperCase() + inst.plan?.slice(1) || 'Basic'}
                    </span>
                  </td>
                  <td className="py-3">
                    <span className="font-semibold text-gray-900">{inst.userCount.toLocaleString()}</span>
                  </td>
                  <td className="py-3">
                    {inst.isActive ? (
                      <span className="flex items-center text-green-600 text-sm">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="flex items-center text-gray-400 text-sm">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Recent Institutions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Recently Added Institutions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {recentInstitutions.map((inst) => (
            <div key={inst.id} className="p-4 border border-gray-100 rounded-lg hover:border-blue-200 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <BuildingOffice2Icon className="h-5 w-5 text-blue-600" />
                <span className={`text-xs px-1.5 py-0.5 rounded ${inst.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {inst.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="font-medium text-gray-900 truncate">{inst.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(inst.createdAt).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        Last updated: {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : 'N/A'}
      </div>
    </div>
  )
}

export default SuperAdminDashboard
